import _ from 'lodash'
import Vue from 'vue'
import i18n from '@/i18n'
import ElementUI, { Select, Tooltip } from 'element-ui'
import '@/assets/scss/element-variables.scss'
import '@/assets/scss/lightTooltip.scss'
import { parseStylePxToInt } from '@/utils/common'

const CustomRemoteSelect = {
  ...Select,
  name: 'CustomRemoteElSelect',
  componentName: 'ElSelect',
  computed: {
    ...Select.computed,
    iconClass () {
      return this.remote && this.loading ? 'loading' : (this.visible ? 'arrow-up is-reverse' : 'arrow-up')
    },
    emptyText () {
      if (this.loading) {
        return this.loadingText || this.t('el.select.loading')
      } else {
        if (this.filterable && this.query && this.options.length > 0 && this.filteredOptionsCount === 0) {
          return this.noMatchText || this.t('el.select.noMatch')
        }
        if (this.options.length === 0) {
          return this.noDataText || this.t('el.select.noData')
        }
      }
      return null
    }
  },
  watch: {
    ...Select.watch,
    loading () {
      if (this.remote && this.visible && !this.loading) {
        this.$nextTick(() => {
          if (this.$refs.popper) {
            this.$refs.popper.updatePopper()
          }
        })
      }
    }
  }
}

/**
 * @param defaultProps 给所有 el-tooltip 的默认 props
 * @param directiveName 指令名称, 默认为 autoTooltip
 * @param contextKey 给目标元素上设置的自定义属性名称，默认为 __auto_tooltip__， 具体数据结构见下方声明的 class TooltipContext
 *
 * 注册一个自定义指令：当内容超出宽度会显示为省略号，并且使用 el-tooltip 组件来根据内容是否超出宽度来自动显示 tooltip
 *
 * 用法： `<div v-auto-tooltip></div>`
 *
 * 可以传入.always修饰符 使得不论内容是否超出宽度都显示tooltip 例如：
 * `<div v-auto-tooltip.always>这段内容没有超出宽度，但还是会显示tip</div>`
 *
 * 可以给指令传值来给内部的 el-tooltip 传入自定义的 props 例如：
 * `<div v-auto-tooltip="{ content: $t('common.tip') placement: 'top', effect: 'light' }"></div>`
 *
 * 如果没有其他自定义样式可以直接传入提示内容 例如：
 * `<div v-auto-tooltip="$t('common.tip')"></div>`
 *
 * 功能：当目标元素的字超出给定的宽度时，超出的部分会自动显示为省略号，
 * 当鼠标悬浮其上时，会自动显示 tooltip，并且在离开时自动关闭 tooltip
 *
 * !!! 指定宽度可以通过 css 来实现自动的宽度，一般不需要强制给目标元素设置宽度
 */
function registerOverflowToolTipDirective (defaultProps, directiveName, contextKey) {
  if (!directiveName) {
    directiveName = 'autoTooltip'
  }
  if (!contextKey) {
    contextKey = '__auto_tooltip__'
  }

  const staticProps = { ...defaultProps }

  const tooltipProps = {
    content: null,
    props: { ...staticProps },
    scopedCssId: null
  }

  const getScopedCssId = (el) => {
    const id = _.findKey(el.dataset, (val, key) => val === '' && key.startsWith('v-'))
    return id
  }

  Vue.use(Tooltip)
  const vm = new Vue({
    render (h) {
      return (<Tooltip ref="tooltip" content={ tooltipProps.content } { ...{ props: tooltipProps.props }}></Tooltip>)
    },
    methods: {
      async updateScopedCssId () {
        await this.$nextTick()
        const popper = this.$refs.tooltip?.$refs.popper
        if (!popper) {
          return
        }
        const scopedCssId = tooltipProps.scopedCssId
        if (popper.dataset[scopedCssId] === '') {
          // 已存在相同的 scopedCssId
          return
        }
        const currentId = getScopedCssId(popper)
        if (currentId) {
          // 移除其他 scopedCssId
          delete popper.dataset[currentId]
        }
        if (scopedCssId) {
          // 添加新的 scopedCssId
          popper.dataset[scopedCssId] = ''
        }
      }
    }
  }).$mount()
  const tooltip = vm.$refs.tooltip

  const activeTooltip = _.debounce(() => {
    tooltip.handleShowPopper()
  }, 50)

  class TooltipContext {
    hasBind = false
    always = false
    content = null
    bindingValue = null
    scopedCssId = null

    constructor (el) {
      this.el = el
    }

    get isOverflow () {
      return this.always || (this.el.scrollWidth > this.el.offsetWidth)
    }

    onMouseEnter = (e) => {
      if (!this.isOverflow) {
        return
      }
      if (tooltip.referenceElm === this.el && tooltip.showPopper) {
        // 鼠标由 margin 区域重新进入目标元素
        tooltip.show()
        return
      }
      const el = this.el

      tooltipProps.scopedCssId = this.scopedCssId
      if (this.bindingValue.popperClass) {
        tooltipProps.props = { ...staticProps, ...this.bindingValue, popperClass: _.join([staticProps.popperClass, this.bindingValue.popperClass], ' ') }
      } else {
        tooltipProps.props = { ...staticProps, ...this.bindingValue }
      }

      tooltipProps.content = this.content || el.innerText || el.textContent

      // 修改 tooltipProps 后强制重新渲染
      vm.$forceUpdate()

      tooltip.referenceElm = el
      tooltip.$refs.popper && (tooltip.$refs.popper.style.display = 'none')
      tooltip.doDestroy() // 防止有缓存，现将已存在页面上的 tooltip 销毁
      tooltip.setExpectedState(true)
      activeTooltip()
      vm.updateScopedCssId()
    }

    onMouseLeave = (e) => {
      if (this.isInTooltipMarginBlank(e)) {
        /**
         * 因为 tooltip 与目标元素之前有 margin， 而且也和这里一样监听的 enter 和 leave 事件
         * 所以当鼠标离开目标元素时，此时鼠标正好在 margin 的区域里，tooltip 自身的事件监听不会触发
         * 使用这个方法来判断是否在 margin 区域里，在的话就不关闭 tooltip
         * 如果不这样做的话，会导致用户无法将鼠标悬停在 tooltip 上，也就无法复制 tooltip 的内容
        */
        return
      }
      tooltip.setExpectedState(false)
      tooltip.handleClosePopper()
    }

    getMarginRect (margin, rect) {
      // 获取 margin 区域的 rect
      const result = { top: rect.top, left: rect.left, bottom: rect.bottom, right: rect.right }
      const key = _.findKey(margin, v => v > 0)
      const relativeKeyMap = { top: 'bottom', left: 'right' }
      let relativeKey = relativeKeyMap[key]
      if (relativeKey) {
        result[key] = rect[key] - margin[key]
        result[relativeKey] = rect[key]
      } else {
        relativeKey = _.findKey(relativeKeyMap, v => v === key)
        result[key] = rect[key]
        result[relativeKey] = rect[key] + margin[key]
      }
      return result
    }

    isInRect (point, rect) {
      /**
       * 判断一个点是否在一个矩形里
       */
      return (
        point.x >= rect.left &&
        point.x <= rect.right &&
        point.y >= rect.top &&
        point.y <= rect.bottom
      )
    }

    isInTooltipMarginBlank (e) {
      const popperEl = tooltip.popperElm
      if (!popperEl) {
        // tooltip 内部的 popper 元素渲染有延时，没有显示在页面上，也自然不需要检测
        return false
      }
      const style = getComputedStyle(popperEl)
      const margin = {
        left: parseStylePxToInt(style.marginLeft),
        right: parseStylePxToInt(style.marginRight),
        top: parseStylePxToInt(style.marginTop),
        bottom: parseStylePxToInt(style.marginBottom)
      }
      const rect = popperEl.getBoundingClientRect()
      const marginRect = this.getMarginRect(margin, rect)
      const inSide = this.isInRect(e, marginRect)
      if (inSide) {
        // 当鼠标在 margin 区域时，需要再次检查鼠标离开后是进入原来的目标元素、进入 tooltip 还是彻底离开
        const recheck = (me) => {
          if (tooltip.showPopper) {
            if (!this.isInRect(me, marginRect)) {
              // 离开 margin 区域
              if (!this.isInRect(me, rect)) {
                // 同时也离开 tooltip 区域，那就之间关闭 tooltip
                // 此时如果是进入了目标元素，那么会重新出发 enter 事件（也就是 onMouseEnter 方法会被重新调用）
                tooltip.setExpectedState(false)
                tooltip.handleClosePopper()
              }
              // 只要离开 margin 区域就关闭事件监听，tooltip 内部有自己的事件监听，鼠标悬浮时不会关闭
              window.removeEventListener('mousemove', recheck)
            }
          } else {
            // 此时 tooltip 已经不显示了，关闭事件监听
            window.removeEventListener('mousemove', recheck)
          }
        }
        window.addEventListener('mousemove', recheck)
      }
      return inSide
    }
  }

  const bindEvent = (el, isBind) => {
    const context = el[contextKey]
    if (isBind && !context.hasBind) {
      el.addEventListener('mouseenter', context.onMouseEnter)
      el.addEventListener('mouseleave', context.onMouseLeave)
      context.hasBind = true
    } else if (!isBind && context.hasBind) {
      el.removeEventListener('mouseenter', context.onMouseEnter)
      el.removeEventListener('mouseleave', context.onMouseLeave)
      context.hasBind = false
    }
  }

  const updateContextAndBindEvent = (el, binding, vnode) => {
    const context = el[contextKey]
    if (_.isString(binding.value)) {
      context.content = binding.value
    }
    const bindingValue = { ...binding.value }
    let isOverflow = el.scrollWidth > el.offsetWidth
    if (binding.modifiers.always === true) {
      isOverflow = true
      context.always = true
    } else {
      context.always = false
    }
    context.scopedCssId = getScopedCssId(el)
    context.bindingValue = bindingValue
    bindEvent(el, isOverflow)
  }

  let styleEl = null
  const checkCss = () => {
    const headEl = document.head
    if (styleEl) {
      // 不使用 Node.contains() 来判断是否已添加 styleEl 是因为：
      // Node.appendChild() 方法将一个节点附加到指定父节点的子节点列表的末尾处。
      // 如果将被插入的节点已经存在于当前文档的文档树中，那么 appendChild() 只会将它从原先的位置移动到新的位置（不需要事先移除要移动的节点）。
      // 这意味着，一个节点不可能同时出现在文档的不同位置。

      // if (headEl.contains(headEl)) {
      //   return
      // }
    } else {
      styleEl = document.createElement('style')
      styleEl.innerHTML = '.auto-tooltip{word-break:keep-all;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}'
    }
    headEl.appendChild(styleEl)
  }

  Vue.directive(directiveName, {
    bind: function (el, binding, vnode) {
      checkCss()
      el.classList.add('auto-tooltip')
      el[contextKey] = new TooltipContext(el)
    },
    unbind: function (el) {
      el.classList.remove('auto-tooltip')
      bindEvent(el, false)
      delete el[contextKey] // 释放内存
      tooltip.setExpectedState(false)
      tooltip.handleClosePopper()
    },
    inserted: updateContextAndBindEvent,
    componentUpdated: updateContextAndBindEvent
  })
}

function setup () {
  Vue.use(ElementUI, {
    i18n: (key, value) => i18n.t(key, value) // 在注册Element时设置i18n的处理方法
  })
  Vue.component(CustomRemoteSelect.name, CustomRemoteSelect)
  registerOverflowToolTipDirective({
    effect: 'light',
    popperClass: 'light-tooltip-popper'
  })
}

export { setup, CustomRemoteSelect }
